import { useCallback, useEffect, useRef, useState } from 'react';

import { NavIcon, type IconName } from './NavIcon';
import {
  THEME_CHOICES,
  applyTheme,
  readStoredTheme,
  storeTheme,
  type ThemeChoice,
} from './theme';

/**
 * System / Light / Dark, as one control.
 *
 * ## Why a radio group and not a switch
 *
 * There are three states and exactly one of them is true, which is what a
 * radio group IS. A two-position switch cannot say "follow the machine", and a
 * three-position switch with `aria-pressed` on each segment tells a screen
 * reader it has met three independent toggles that happen to be next to each
 * other — so somebody who cannot see the fill has no way to know that choosing
 * one un-chooses the others. `role="radiogroup"` says it in one word.
 *
 * ## Keyboard
 *
 * One tab stop for the whole group (the roving `tabIndex`), then the arrow
 * keys move between the three and CHOOSE as they go, with Home and End for the
 * ends — the pattern every native radio group has and the one a person will
 * already have in their fingers. Space and Enter also choose, because a
 * `<button>` does that for free and taking it away would be a surprise.
 *
 * Selecting with the arrow keys means the theme changes as you walk the group,
 * which is right here: the control's whole subject is what the page looks
 * like, and seeing it is how you decide.
 *
 * ## Where the state lives
 *
 * In `localStorage` and on `<html>`, not in a context. Two of these are
 * mounted at most (the sidebar's and the public bar's) and never at the same
 * time — the shells are mutually exclusive — so a provider would be a wrapper
 * around one consumer. The initial value is read from storage on mount, which
 * is the same value the inline script in `index.html` already stamped, so the
 * control opens showing what the page is actually wearing.
 */

const OPTIONS: { value: ThemeChoice; label: string; icon: IconName }[] = [
  { value: 'system', label: 'System', icon: 'theme-system' },
  { value: 'light', label: 'Light', icon: 'theme-light' },
  { value: 'dark', label: 'Dark', icon: 'theme-dark' },
];

export function ThemeToggle(): React.JSX.Element {
  const [choice, setChoice] = useState<ThemeChoice>('system');
  const groupRef = useRef<HTMLDivElement>(null);

  /* Read once, after mount. Not `useState(readStoredTheme)`: this component
     renders on the server side of nothing today, but `localStorage` in a lazy
     initialiser is still a read during render, and the value it would produce
     is the value the inline script already applied. */
  useEffect(() => {
    setChoice(readStoredTheme());
  }, []);

  const pick = useCallback((next: ThemeChoice) => {
    setChoice(next);
    storeTheme(next);
    applyTheme(next);
  }, []);

  /* While the choice is System, the machine can change its mind — a laptop on
     a schedule flips at sunset — and the page has to follow it without a
     reload. The stylesheet already does (it is a media query), but the
     `theme-color` meta is set from JavaScript, so it needs telling. */
  useEffect(() => {
    if (choice !== 'system') return;
    if (typeof window.matchMedia !== 'function') return;
    const query = window.matchMedia('(prefers-color-scheme: light)');
    const onChange = (): void => applyTheme('system');
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, [choice]);

  const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>): void => {
    const keys = ['ArrowRight', 'ArrowDown', 'ArrowLeft', 'ArrowUp', 'Home', 'End'];
    if (!keys.includes(event.key)) return;
    event.preventDefault();

    const at = THEME_CHOICES.indexOf(choice);
    const last = THEME_CHOICES.length - 1;
    let next: number;
    if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = last;
    else if (event.key === 'ArrowRight' || event.key === 'ArrowDown')
      next = at === last ? 0 : at + 1;
    else next = at === 0 ? last : at - 1;

    const value = THEME_CHOICES[next]!;
    pick(value);
    /* Focus follows selection, the way it does in a native radio group. The
       button is found by its value rather than by index, so it keeps working
       if the order ever changes. */
    groupRef.current
      ?.querySelector<HTMLButtonElement>(`button[data-theme-value="${value}"]`)
      ?.focus();
  };

  return (
    <div
      ref={groupRef}
      className="theme-toggle"
      role="radiogroup"
      aria-label="Theme"
      onKeyDown={onKeyDown}
    >
      {OPTIONS.map((option) => {
        const selected = option.value === choice;
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={selected}
            /* One tab stop for the group: Tab reaches the chosen one, the
               arrows move inside. */
            tabIndex={selected ? 0 : -1}
            data-theme-value={option.value}
            className="theme-option"
            onClick={() => pick(option.value)}
          >
            <NavIcon name={option.icon} size={15} />
            <span className="theme-option-label">{option.label}</span>
          </button>
        );
      })}
    </div>
  );
}
